import React, { useState, useEffect, useCallback } from "react";
import { useLanguage } from "../contexts/LanguageContext";
import { getUserProfile, updateUserProfile } from "../api/user";
import DashboardLayout from "../components/DashboardLayout";
import LocationPicker from "../components/LocationPicker";
import "../styles/main.css";
import "../styles/ProfilePage.css";

const translations = {
  en: {
    profile: "Profile Settings",
    personalInfo: "Personal Information",
    email: "Email Address",
    name: "Display Name",
    createdAt: "Member Since",
    save: "Save Changes",
    loading: "Loading profile...",
    error: "Error loading profile",
    success: "Profile updated successfully!",
    notLoggedIn: "You must be logged in to view this page.",
    login: "Log In",
    birthInfo: "Celestial Birth Details",
    birthTimeParts: "Birth Time",
    country: "Country of Birth",
    city: "City of Birth",
    mbti: "Cosmic MBTI",
    gender: "Gender at Birth",
    male: "Male",
    female: "Female",
    prefLang: "Preferred Language",
    notSet: "Not set"
  },
  zh: {
    profile: "个人设置",
    personalInfo: "个人信息",
    email: "电子邮箱",
    name: "显示名称",
    createdAt: "注册时间",
    save: "保存更改",
    loading: "正在加载资料...",
    error: "加载资料失败",
    success: "资料更新成功！",
    notLoggedIn: "您必须登录才能查看此页面。",
    login: "登录",
    birthInfo: "星命出生详情",
    birthTimeParts: "出生时间 (年/月/日/时/分)",
    country: "出生国家",
    city: "出生城市",
    mbti: "星际人格 (MBTI)",
    gender: "出生性别",
    male: "男",
    female: "女",
    prefLang: "首选语言",
    notSet: "未设置"
  }
};

export default function ProfilePage({ session }) {
  const { language } = useLanguage();
  const t = translations[language] || translations.en;
  const isChinese = language === "zh";

  // --- 1. Constants for Time Dropdowns ---
  const hourOptions = isChinese
    ? [...Array(24).keys()].map(h => ({ value: h, label: h.toString().padStart(2, "0") }))
    : [...Array(12).keys()].map(h => {
        const hour = h === 0 ? 12 : h;
        return { value: hour, label: hour.toString().padStart(2, "0") };
      });

  const minuteOptions = [...Array(60).keys()].map(m => ({
    value: m,
    label: m.toString().padStart(2, "0")
  }));

  // --- 2. State Management ---
  const [profile, setProfile] = useState(null);
  const [name, setName] = useState("");
  const [gender, setGender] = useState("");
  const [dobYear, setDobYear] = useState("");
  const [dobMonth, setDobMonth] = useState("");
  const [dobDay, setDobDay] = useState("");
  const [dobHour, setDobHour] = useState("");
  const [dobMin, setDobMin] = useState("");
  const [amPm, setAmPm] = useState("AM"); //
  const [country, setCountry] = useState("");
  const [city, setCity] = useState("");
  const [mbtiType, setMbtiType] = useState("");
  const [prefLang, setPrefLang] = useState("");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("");

  // --- 3. Helpers ---
  /* Inside ProfilePage.js */
const populateDobParts = useCallback((dobStr) => {
  if (!dobStr) return;
  try {
    const normalized = dobStr.replace('T', ' ');
    const [datePart, timePart] = normalized.split(" ");
    const [y, m, d] = datePart.split("-");
    const [h, min] = (timePart || "00:00:00").split(":");
    
    // Set Year, Month, Day directly
    setDobYear(y || "");
    setDobMonth(m || "");
    setDobDay(d || "");
    setDobMin(min || "00");

    const hourNum = parseInt(h);
    
    // Logic to handle 12h display and AM/PM state
    if (!isChinese) {
      const currentAmPm = hourNum >= 12 ? "PM" : "AM";
      setAmPm(currentAmPm);
      
      // Convert 0-23 back to 1-12 for the dropdown
      const displayHour = hourNum % 12 || 12;
      setDobHour(displayHour.toString());
    } else {
      setDobHour(hourNum.toString());
    }
  } catch (e) {
    console.warn("Failed to parse date_of_birth:", dobStr, e);
  }
}, [isChinese]);

  // --- 4. Data Loading ---
  useEffect(() => {
    if (!session) {
      setLoading(false);
      return;
    }

    async function loadProfile() {
  try {
    const token = session.access_token;
    const data = await getUserProfile(token);
    
    setProfile(data);
    setName(data.name || "");
    setGender(data.gender_at_birth || "");
    setPrefLang(data.language || language);
    
    // FIX: Match the keys returned in your user.js API
    setCountry(data.country_of_birth || ""); 
    setCity(data.city_of_birth || "");
    setMbtiType(data.mbti || ""); 

    populateDobParts(data.date_of_birth);
  } catch (err) {
    console.error(err);
    setMessage(t.error);
    setMessageType("error");
  } finally {
    setLoading(false);
  }
}
    loadProfile();
  }, [session, t.error, populateDobParts, language]);

  // --- 5. Save Handler ---
  /* Inside ProfilePage.js */
// 1. Fixed handleSave Logic
const handleSave = async (e) => {
  e.preventDefault();
  setSaving(true);
  setMessage("");
  
  try {
    const token = session.access_token;
    
    // Calculate 24-hour hour based on AM/PM
    let finalHour = Number(dobHour);
    if (!isChinese) {
      if (amPm === "PM" && finalHour < 12) finalHour += 12;
      if (amPm === "AM" && finalHour === 12) finalHour = 0;
    }

    // Ensure all parts are padded strings
    const monthStr = dobMonth.toString().padStart(2, '0');
    const dayStr = dobDay.toString().padStart(2, '0');
    const hourStr = finalHour.toString().padStart(2, '0');
    
    // THE FIX: Ensure dobMin is actually used
    const minStr = dobMin.toString().padStart(2, '0');

    // Final String format for Supabase TIMESTAMPTZ
    const dateOfBirth = `${dobYear}-${monthStr}-${dayStr} ${hourStr}:${minStr}:00`;

    // Send to API in the exact order defined in user.js
    await updateUserProfile(
      token, 
      name, 
      prefLang, 
      dateOfBirth, 
      gender, 
      country, 
      city, 
      mbtiType
    );
    
    const fresh = await getUserProfile(token);
    setProfile(fresh);
    setMessage(t.success);
    setMessageType("success");
  } catch (err) {
    console.error("Save failed:", err);
    setMessage(t.error);
    setMessageType("error");
  } finally {
    setSaving(false);
  }
};

  if (!session) return <div className="loading-container"><h2>{t.notLoggedIn}</h2></div>;
  if (loading) return <DashboardLayout session={session}><div className="loading-container"><p>{t.loading}</p></div></DashboardLayout>;

  return (
    <DashboardLayout session={session}>
      <div className="profile-container">
        <h1 className="profile-header">{t.profile}</h1>
        <div className="profile-card">
          <div className="profile-form-content">
            <form onSubmit={handleSave}>
              <div className="profile-section-header"><h3 className="profile-section-title">{t.personalInfo}</h3></div>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">{t.email}</label>
                  <input type="email" value={profile?.email || session.user.email} disabled className="form-input disabled-input" />
                </div>
                <div className="form-group">
                  <label className="form-label">{t.name}</label>
                  <input type="text" value={name} onChange={(e) => setName(e.target.value)} className="form-input" />
                </div>
                <div className="form-group">
                  <label className="form-label">{t.prefLang}</label>
                  <select value={prefLang} onChange={(e) => setPrefLang(e.target.value)} className="form-select">
                    <option value="en">English</option>
                    <option value="zh">中文</option>
                  </select>
                </div>
              </div>

              <hr className="section-divider" />

              <div className="profile-section-header"><h3 className="profile-section-title">{t.birthInfo}</h3></div>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">{t.mbti}</label>
                  <select value={mbtiType} onChange={(e) => setMbtiType(e.target.value)} className="form-select stellar-select">
                    <option value="">{t.notSet}</option>
                    {["INTJ", "INTP", "ENTJ", "ENTP", "INFJ", "INFP", "ENFJ", "ENFP", "ISTJ", "ISFJ", "ESTJ", "ESFJ", "ISTP", "ISFP", "ESTP", "ESFP"].map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">{t.gender}</label>
                  <select value={gender} onChange={(e) => setGender(e.target.value)} className="form-select">
                    <option value="">{t.notSet}</option>
                    <option value="male">{t.male}</option>
                    <option value="female">{t.female}</option>
                  </select>
                </div>

                <LocationPicker 
                  country={country} city={city} language={language} t={t}
                  onCountryChange={(newCountry, firstCity) => { setCountry(newCountry); setCity(firstCity); }}
                  onCityChange={(newCity) => setCity(newCity)}
                />

<div className="form-group full-width">
      <label className="form-label">{t.birthTimeParts}</label>
      
      {/* Mirroring UserForm's date input */}
      <input
        type="date"
        value={dobYear && dobMonth && dobDay ? `${dobYear}-${dobMonth.toString().padStart(2, '0')}-${dobDay.toString().padStart(2, '0')}` : ""}
        onChange={(e) => {
          const [y, m, d] = e.target.value.split('-');
          setDobYear(y); setDobMonth(m); setDobDay(d);
        }}
        className="form-input"
        style={{ marginBottom: '15px' }}
      />

      {/* Mirroring UserForm's time-picker flex container */}
      <div className="time-picker">
        <select value={dobHour} onChange={e => setDobHour(e.target.value)} className="form-select" style={{flex: 1, textAlign: 'center'}}>
          <option value="">{isChinese ? "时" : "HH"}</option>
          {hourOptions.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>

        <span style={{ alignSelf: 'center', marginBottom: '20px', fontWeight: 'bold', padding: '0 5px' }}>:</span>

        <select value={dobMin} onChange={e => setDobMin(e.target.value)} className="form-select" style={{flex: 1, textAlign: 'center'}}>
          <option value="">{isChinese ? "分" : "MM"}</option>
          {minuteOptions.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>

        {!isChinese && (
          <select value={amPm} onChange={e => setAmPm(e.target.value)} className="form-select" style={{flex: 1, textAlign: 'center', marginLeft: '10px'}}>
            <option value="AM">AM</option>
            <option value="PM">PM</option>
          </select>
        )}
      </div>
    </div>
              </div>    

              <div className="form-actions">
                <button type="submit" disabled={saving} className="save-button primary">{saving ? "..." : t.save}</button>
                {message && <span className={`message ${messageType}`}>{message}</span>}
              </div>
            </form>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}